import { type BaseButtonProps, Button } from '@repo/design-system-kit/components/button/button';
import { IconCheckmarkRoundedFilledSmall } from '@repo/design-system-kit/assets/icons/icon-checkmark-rounded-filled-small';
import React from 'react';
import { type ButtonProps as AriaButtonProps } from 'react-aria-components';
import type { MediaQuery } from '#hooks/use-media';
import type { CustomPricePlanProps } from '../custom-price-plan/custom-price-plan.tsx';
import type { AnnualProps, MonthlyProps } from './price-plan';
import { PricePlansContainer } from './price-plans-container.tsx';
import styles from './price-plans-container.bridge.module.css';

type MobileBreakpoint = Extract<MediaQuery, 'md' | 'lg'>;

interface MainButtonBridge {
  text: string;
  buttonProps: BaseButtonProps & AriaButtonProps;
}

type MonthlyPropsBridge = Omit<MonthlyProps, 'descriptionCta' | 'mainButton' | 'priceCta'> & {
  lineup: 'solo' | 'teams';
  descriptionCta?: {
    text: string;
    onPress: () => void;
  };
  mainButton: MainButtonBridge;
  priceCta?: {
    text: string;
    onPress: () => void;
  };
};

type AnnualPropsBridge = Omit<AnnualProps, 'mainButton'> & {
  lineup: 'solo' | 'teams';
  mainButton: MainButtonBridge;
};

export type PricePlansBridgeProps = (MonthlyPropsBridge | AnnualPropsBridge)[];

type CustomPricePlanBridgeProps = Omit<CustomPricePlanProps, 'mainButton'> & {
  mainButton: ({ type: 'button' } & MainButtonBridge) | { type: 'success'; text: string };
};

export interface PricePlansContainerBridgeProps {
  pricePlans: PricePlansBridgeProps;
  customPricePlan?: CustomPricePlanBridgeProps;
  hasPromoInAnyProducts?: boolean;
  hidePromoHighlights?: boolean;
  hidePromoInAllProducts?: boolean;
  layout?: 'grid' | 'smallGrid';
  isCompactLayout?: boolean;
  mobileBreakpoint?: MobileBreakpoint;
  sharedAddons?: {
    title: string;
    addons: {
      description: string;
      iconLightPath: string;
      iconDarkPath: string;
    }[];
  };
  includedInAllPlans?: {
    title: string;
    benefits: string[];
  };
  comparePlansLink?: {
    text: string;
    href: string;
  };
}

export function PricePlansContainerBridge({
  pricePlans,
  customPricePlan,
  hasPromoInAnyProducts,
  hidePromoInAllProducts,
  layout = 'grid',
  ...props
}: PricePlansContainerBridgeProps): React.JSX.Element {
  const mappedPricePlans = pricePlans.map(plan => {
    const mainButton = (
      <Button stretch data-testid="price-plan-choose-plan-button" {...plan.mainButton.buttonProps}>
        {plan.mainButton.text}
      </Button>
    );

    if (plan.recurrence === 'annual') {
      return { ...plan, mainButton };
    }

    const hasPreselectedInLineup = pricePlans.some(p =>
      p.traits.some(trait => trait.name === 'preselected')
    );

    return {
      ...plan,
      descriptionCta:
        !hasPromoInAnyProducts && plan.descriptionCta ? (
          <Button onPress={plan.descriptionCta.onPress} variant="tertiary">
            {plan.descriptionCta.text}
          </Button>
        ) : null,
      mainButton,
      priceCta:
        hasPreselectedInLineup && plan.priceCta ? (
          <Button className={styles.priceCta} onPress={plan.priceCta.onPress} variant="tertiary">
            {plan.priceCta.text}
          </Button>
        ) : null,
    };
  });

  if (customPricePlan) {
    return (
      <PricePlansContainer
        customPricePlan={{
          ...customPricePlan,
          mainButton:
            customPricePlan.mainButton.type === 'button' ? (
              <Button stretch variant="secondary" {...customPricePlan.mainButton.buttonProps}>
                {customPricePlan.mainButton.text}
              </Button>
            ) : (
              // customPricePlan.mainButton.type === 'success'
              <div className={styles.success}>
                <IconCheckmarkRoundedFilledSmall className={styles.successIcon} />
                <span className="body-standard">{customPricePlan.mainButton.text}</span>
              </div>
            ),
        }}
        hasPromoInAnyProducts={hasPromoInAnyProducts}
        hidePromoInAllProducts={hidePromoInAllProducts}
        layout={layout}
        pricePlans={mappedPricePlans}
        {...props}
      />
    );
  }

  return (
    <PricePlansContainer
      hasPromoInAnyProducts={hasPromoInAnyProducts}
      hidePromoInAllProducts={hidePromoInAllProducts}
      layout={layout}
      pricePlans={mappedPricePlans}
      {...props}
    />
  );
}
